import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Heart, Sparkles, Star } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Newsletter } from "@/components/Newsletter";
import { LikeButton } from "@/components/LikeButton";
import { PregnancyCalculator } from "@/components/PregnancyCalculator";
import { SiteShare } from "@/components/SiteShare";
import useSEO from "@/hooks/useSEO";
import useJsonLd from "@/hooks/useJsonLd";
import api from "@/lib/api";

const SITE = process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL || "";

const HERO_IMG =
  "https://images.pexels.com/photos/10956985/pexels-photo-10956985.jpeg?auto=compress&cs=tinysrgb&w=760";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" } }),
};

export default function Home() {
  const [blogs, setBlogs] = useState([]);
  const [blogLikes, setBlogLikes] = useState({});
  const [pageLikes, setPageLikes] = useState(0);
  useSEO({
    title: "Warm Support for Breastfeeding & Pumping Mothers",
    description:
      "MommysLiquidGold offers honest, science-backed guidance for breastfeeding and pumping mothers — the benefits of breastfeeding, real nursing struggles, lactation help, and the best breast pumps to make feeding your baby easier.",
    keywords: ["breastfeeding", "breastfeed", "breastfed", "breastfed baby", "nursing baby", "pumping", "lactation", "benefits of breastfeeding", "best breast pump", "breastfeeding support"],
    image: `${process.env.REACT_APP_BACKEND_URL}/api/og-image/page/home.png`,
    path: "/",
  });

  useJsonLd("website", {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "MommysLiquidGold",
    "url": `${SITE}/`,
    "description": "Warm, science-backed support for breastfeeding mothers.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE}/blog?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  });
  useJsonLd("organization", {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MommysLiquidGold",
    "url": `${SITE}/`,
    "logo": `${SITE}/favicon.ico`,
    "description": "Honest, warm support for breastfeeding mothers — benefits, struggles, and curated gear.",
  });

  useEffect(() => {
    api.get("/blogs").then((r) => {
      const top = r.data.slice(0, 3);
      setBlogs(top);
      const ids = top.map((b) => b.slug).join(",");
      if (ids) {
        api.get(`/likes?type=blog&ids=${encodeURIComponent(ids)}`).then((lr) => setBlogLikes(lr.data || {})).catch(() => {});
      }
    }).catch(() => {});
    api.get("/likes?type=page&ids=home").then((r) => setPageLikes(r.data?.home || 0)).catch(() => {});
  }, []);

  return (
    <div className="App">
      <Navbar />
      <main>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="blob bg-accent h-80 w-80 -top-20 -right-10" />
        <div className="blob bg-secondary/40 h-72 w-72 top-40 -left-16" />
        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-semibold text-accent-foreground mb-6">
              <Sparkles className="h-4 w-4" /> Real support, no judgment
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
              Breastfeeding is beautiful.
              <span className="text-primary"> It's also hard.</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
              We're here for every part of it — the science-backed benefits, the 3 a.m. struggles,
              and the honest reviews of pumps and gear that actually make life easier.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/blog"
                data-testid="hero-read-blog"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-7 py-3.5 hover:-translate-y-0.5 transition-transform soft-shadow"
              >
                Read the Blog <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/best-breast-pumps"
                data-testid="hero-view-pumps"
                className="inline-flex items-center gap-2 rounded-full bg-card border border-border font-bold px-7 py-3.5 hover:-translate-y-0.5 transition-transform"
              >
                Best Breast Pumps
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <img
              src={HERO_IMG}
              alt="Mother lovingly breastfeeding her baby"
              width="760" height="950" loading="eager" fetchPriority="high" decoding="async"
              className="rounded-3xl object-cover w-full aspect-[4/5] soft-shadow"
            />
            <div className="absolute -bottom-5 -left-5 bg-card rounded-2xl px-5 py-4 soft-shadow border border-border">
              <div className="flex items-center gap-1 text-primary mb-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4" fill="currentColor" />)}
              </div>
              <p className="text-sm font-semibold">Loved by 12,000+ mamas</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-6xl mx-auto px-5 py-8 grid gap-5 md:grid-cols-3">
        {[
          { icon: Shield, title: "Science-backed", text: "Benefits and guidance grounded in real research, explained warmly." },
          { icon: Heart, title: "Honestly supportive", text: "We talk about the hard parts too — because you deserve the truth." },
          { icon: Sparkles, title: "Curated gear", text: "Independent picks for the best breast pumps for every lifestyle." },
        ].map((c, i) => (
          <motion.div
            key={c.title}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fade}
            className="bg-card rounded-2xl border border-border p-7 soft-shadow"
          >
            <span className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center mb-4">
              <c.icon className="h-5 w-5 text-primary" />
            </span>
            <h2 className="font-heading text-xl font-semibold mb-2">{c.title}</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">{c.text}</p>
          </motion.div>
        ))}
      </section>

      {/* Latest articles */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">From the Blog</h2>
            <p className="text-muted-foreground mt-2">Fresh reads for your feeding journey.</p>
          </div>
          <Link to="/blog" data-testid="view-all-blogs" className="text-primary font-semibold hover:underline hidden sm:inline">
            View all →
          </Link>
        </div>
        <div className="grid gap-7 md:grid-cols-3">
          {blogs.map((b, i) => (
            <motion.div key={b.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
              <div className="group flex flex-col h-full bg-card rounded-2xl border border-border overflow-hidden soft-shadow hover:-translate-y-1 transition-transform">
                <Link
                  to={`/blog/${b.slug}`}
                  data-testid={`home-blog-${b.slug}`}
                  className="block"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={b.cover_image} alt={b.title} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6 pb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary">{b.category}</span>
                    <h3 className="font-heading text-xl font-semibold mt-2 mb-2 leading-snug">{b.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                  </div>
                </Link>
                <div className="px-6 pb-5 mt-auto">
                  <LikeButton type="blog" itemId={b.slug} count={blogLikes[b.slug] || 0} label="Love this" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <PregnancyCalculator />

      {/* Show some love */}
      <section className="max-w-3xl mx-auto px-5 pb-4">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fade}
          className="text-center bg-accent/60 rounded-3xl border border-border p-9 soft-shadow"
        >
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">Feeling the love?</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Tap the heart to show some love for MommysLiquidGold — it means the world to us mamas.
          </p>
          <div className="flex justify-center">
            <LikeButton type="page" itemId="home" count={pageLikes} size="lg" label="Show some love" />
          </div>
          <div className="mt-7 pt-6 border-t border-border/60">
            <p className="text-sm font-semibold mb-3">Know a mama who'd love this? Share MommysLiquidGold</p>
            <SiteShare align="center" />
          </div>
        </motion.div>
      </section>

      <Newsletter />
      </main>
      <Footer />
    </div>
  );
}
